import { UserAnswersObject, UserAnswersObjectKeys } from './userAnswers';
import { UserQuestion } from './questions';
import { InputValidateOptions } from '../components/fieldTypes/FieldType.component';

/**
 * Checks if string answers meet backend requirements
 * @param {string} value - user's questionnaire answers
 * @param {InputValidateOptions} [validation] - optional validation requirements
 */
export const areStringAnswerRequirementsMet = (
    value: string,
    validation?: InputValidateOptions
) => {
    // If a value doesn't have a validation associated with it, it is automatically true
    if (!validation) {
        return true;
    }

    if (validation?.pattern && validation?.maxLength) {
        return (
            new RegExp(validation.pattern, 'i').test(value) &&
            (value || '').length < validation.maxLength
        );
    }

    if (validation?.pattern) {
        return new RegExp(validation.pattern, 'i').test(value);
    }

    if (validation?.maxLength) {
        return (value || '').length <= validation.maxLength;
    }

    return true;
};

/**
 * Checks if user has answered all the questions
 * @param {UserAnswersObject} answers - user's questionnaire answers
 * @param {UserQuestion} curQuestions - current question
 */
export const userQuestionAnswerValid = (
    answers: UserAnswersObject,
    curQuestions: UserQuestion
) => {
    // Checks if main answer is valid according to constraints
    const curMainAnswer =
        answers[curQuestions.key as UserAnswersObjectKeys] || '';

    const isMainAnswerValid = areStringAnswerRequirementsMet(
        typeof curMainAnswer === 'string' ? curMainAnswer : '',
        curQuestions?.validation?.inputValidationRules
    );
    if (!isMainAnswerValid) return false;

    // Blocking currently only occurs on main questions
    const isQuestionBlocked =
        curQuestions?.requireAlert &&
        curQuestions.requireAlert.condition ===
            answers[curQuestions.key as UserAnswersObjectKeys];
    if (isQuestionBlocked) return false;

    // If main question is answered and has no follow up questions
    const followUpQuestions: Array<any> | undefined =
        curQuestions?.followUpQuestions;
    const isMainQuestionAnswered =
        !!answers[curQuestions.key as UserAnswersObjectKeys];
    if (!followUpQuestions) return isMainQuestionAnswered;

    // If there are follow up questions, check if they meet the conditions
    // to move on
    const areFollowUpQuestionsAnswered = followUpQuestions.every(
        (question: any) => {
            const isFollowUpQuestionAnswered =
                !!answers[question.key as UserAnswersObjectKeys];

            // Checks if follow-up answer is valid according to constraints
            const followUpAnswer =
                answers[question.key as UserAnswersObjectKeys] || '';

            const isAnswerValid = areStringAnswerRequirementsMet(
                typeof followUpAnswer === 'string' ? followUpAnswer : '',
                question?.validation?.inputValidationRules
            );

            // Checks questions that will affect this question
            if (question.condition === null) {
                return isFollowUpQuestionAnswered && isAnswerValid;
            } else {
                const conditionsAreMet =
                    answers[question.condition.key as UserAnswersObjectKeys] ===
                    question.condition.answer;

                if (conditionsAreMet) {
                    return isFollowUpQuestionAnswered && isAnswerValid;
                }

                return true;
            }
        }
    );

    return isMainQuestionAnswered && areFollowUpQuestionsAnswered;
};
